import { useEffect, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import {
  Dimensions,
  StyleSheet,
  Image,
  TouchableOpacity,
  Modal,
  View
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ZCLUB_API_URL } from '../config/api';
import { makeAuthenticatedApiCall } from '../services/authApi';
const { width, height } = Dimensions.get('window');

export function useBannerWithPopUp( wrapperCustomStyle = {}, bannerCustomStyle = {}, screenName = null ) {
  const [bannerId, setBannerId] = useState(null);
  const [bannerImageURL, setBannerImageURL] = useState(null);
  const [bannerRatio, setBannerRatio] = useState(1);
  const [popUpImageURL, setPopUpImageURL] = useState(null);
  const [popUpRatio, setPopUpRatio] = useState(1);
  const [popUpVisible, setPopUpVisible] = useState(false);
  const [externalURL, setExternalURL] = useState(null);
  const route = useRoute();

  useEffect(() => {
    loadBanner();
  }, []);

  useEffect(() => {
    if (bannerImageURL && URL.canParse(bannerImageURL)) {
      Image.getSize( bannerImageURL, (width, height) => {
        if(width && height) {
          setBannerRatio(width / height);
        } else {
          setBannerRatio(1);
        }
      } );
    }
  }, [bannerImageURL]);

  useEffect(() => {
    if (popUpImageURL && URL.canParse(popUpImageURL)) {
      Image.getSize( popUpImageURL, (width, height) => {
        if(width && height) {
          setPopUpRatio(width / height);
        } else {
          setPopUpRatio(1);
        }
      } );
    }
  }, [popUpImageURL]);

  const loadBanner = async () => {
    fetch(`${ZCLUB_API_URL}/banner/screen/${(screenName || route.name)}`, {method: 'GET'})
      .then(response => response.json())
      .then(result => {
        if (result.status === `success` && result.data) {
          const banner = result.data?.banner;
          if (banner) {
            setBannerId(banner.id);
            setBannerImageURL(banner.primary_image);
            if (banner.secondary_image) {
              setPopUpImageURL(banner.secondary_image);
            }
            if (banner.external_link) {
              setExternalURL(banner.external_link);
            }
          }
        }
      })
      .catch(error => {
        console.log((error.message || 'An error occured while getting active banner'));
      })
  }

  const handleBannerPress = () => {
    if (! popUpImageURL) {
      if (! externalURL) {
        return;
      } else {
        handleExternalURL();
        return;
      }
    }

    setPopUpVisible(true);

    // save log
    if (bannerId) {
      try {
        makeAuthenticatedApiCall(
          `${ZCLUB_API_URL}/banner/log/${bannerId}`,
          { 
            method: 'POST',
            body: JSON.stringify({
              'screen_name': (screenName || route.name)
            }),
          },
        );
      } catch (error) {
        console.log(error.message || `An error occured during saving banner interaction log`);
      }
    }
  }

  const handleExternalURL = () => {
    if (externalURL && URL.canParse(externalURL)) {
      window.open(externalURL, `_blank`);
    }
  }

  const hidePopUpModal = () => {
    setPopUpVisible(false);
  }

  const bannerWrapper = bannerImageURL ? (
    <View style={[wrapperCustomStyle]}>
      <TouchableOpacity style={{ width: '100%' }} onPress={handleBannerPress} activeOpacity={0.8}>
        <Image 
          source={{ uri: bannerImageURL }} 
          style={[styles.banner, { aspectRatio: bannerRatio }, bannerCustomStyle]} 
          resizeMode={"aspectRatio" in bannerCustomStyle ? "cover" : "contain"} 
        />
      </TouchableOpacity>

      <Modal
        animationType="fade"
        transparent={true}
        visible={popUpVisible}
        onRequestClose={hidePopUpModal}
      >
        <View style={styles.customModalOverlay}>
          <View style={styles.customModalContainer}>
            <TouchableOpacity style={styles.modalClose} onPress={hidePopUpModal} activeOpacity={0.8}>
              <Ionicons name="close" size={16} color="#000000" />
            </TouchableOpacity>

            {externalURL ? (
              <TouchableOpacity style={[styles.modalImage, {aspectRatio: popUpRatio}]} onPress={handleExternalURL} activeOpacity={0.8}>
                <Image 
                  source={{ uri: popUpImageURL }} 
                  style={[{ aspectRatio: popUpRatio }]} 
                  resizeMode="contain" 
                />
              </TouchableOpacity>
            ) : (
              <Image 
                source={{ uri: popUpImageURL }} 
                style={[{ aspectRatio: popUpRatio }]} 
                resizeMode="contain" 
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  ) : null;

  return {
    bannerWrapper
  };
}

const styles = StyleSheet.create({
  banner: {
    width: '100%',
    borderRadius: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  },
  customModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    zIndex: 11000,
  },
  customModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    maxWidth: Math.min(390, width),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
    position: 'relative',
  },
  modalClose: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    zIndex: 1,
  },
  modalImage: {
    width: '100%',
  },
});